import { inject, injectable } from 'inversify'
import { cloneDeep } from 'lodash-es'
import type { Line } from 'three'
import {
  type CadRenderInvalidator,
  NOOP_CAD_RENDER_INVALIDATOR,
} from '@/plugins/cad/module/CADModule/renderInvalidation'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from './auxiliaryTool/auxiliaryMouseHelper'
import type { CADController } from './CADController'
import { DeleteEntityCommand } from './command/DeleteEntityCommand'
import { UpdateEntityCommand } from './command/UpdateEntityCommand'
import { type EntityGrip, EntityGripHandler } from './edit/entityGripHandler'
import { EntityGripOverlay } from './edit/entityGripOverlay'
import { UpdateEntityByGripCommand } from './edit/updateEntityByGripCommand'
import type { CADViewManager } from './manager/CADViewManager'
import type { HistoryManager } from './manager/HistoryManager'
import { ObjectManager } from './manager/ObjectManager'
import { EntityType } from './model/common/constant'
import type { IPoint } from './model/common/interface'
import type { IEntity, MLeaderEntity } from './model/entity'
import type { EntitySelector } from './selector/entitySelector'

/** CAD 默认工具：探测、选择、控制点编辑、文本编辑 */
@injectable()
export class CADDefaultTool extends BaseTool {
  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  @inject(TYPES.RenderInvalidator)
  private readonly renderInvalidator: CadRenderInvalidator = NOOP_CAD_RENDER_INVALIDATOR

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private gripOverlay?: EntityGripOverlay
  private gripPreviewObjectManager?: ObjectManager
  private gripPreviewLine?: Line
  private grips: EntityGrip[] = []
  private hoverGripId: string | null = null
  private activeGripEdit: {
    entity: IEntity
    grip: EntityGrip
    originalData: Record<string, any>
    targetPoint: IPoint
  } | null = null
  private activeTextEdit: {
    entity: MLeaderEntity
    originalContents: string
    inputElement: HTMLTextAreaElement
  } | null = null

  private readonly handleUndoRedo = () => {
    if (this.activeGripEdit) {
      this.cancelGripEdit()
    }
    if (this.activeTextEdit) {
      this.finishTextEdit(false)
    }
    this.refreshGrips()
  }

  protected initialize(): void {
    this.gripOverlay = new EntityGripOverlay(this.controller, this.viewManager)
    this.gripPreviewObjectManager = new ObjectManager()
    this.viewManager.attachTempObject(this.gripPreviewObjectManager.getSceneGroup())
    this.inject(this.entitySelector)
    this.entitySelector.resetConfig()
    this.historyManager.on('undoRedo', this.handleUndoRedo)
    this.refreshGrips()
  }

  protected terminate(): void {
    this.historyManager.off('undoRedo', this.handleUndoRedo)
    this.cancelGripEdit()
    this.finishTextEdit(true)
    this.gripOverlay?.dispose()
    this.clearGripPreviewLine()
    this.gripPreviewObjectManager?.dispose()
    this.gripPreviewObjectManager?.getSceneGroup().removeFromParent()
    this.gripOverlay = undefined
    this.gripPreviewObjectManager = undefined
    this.grips = []
    this.hoverGripId = null
    this.eject(this.entitySelector)
  }

  /** 暂停默认工具：退出所有进行中的编辑并释放选择器 */
  onPause() {
    this.cancelGripEdit()
    this.finishTextEdit(true)
    this.clearGripHover()
    this.clearGripPreview()
    this.gripOverlay?.clear()
    this.entitySelector.resetConfig()
    this.eject(this.entitySelector)
  }

  /** 恢复默认工具：重新挂载选择器并刷新控制点 */
  onPlay() {
    this.inject(this.entitySelector)
    this.entitySelector.resetConfig()
    this.refreshGrips()
  }

  /**
   * 鼠标按下事件：右键取消控制点编辑，左键触发控制点编辑开始/提交。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (this.activeTextEdit) {
      return
    }

    if (params.event.button === 2) {
      if (this.activeGripEdit) {
        this.cancelGripEdit()
      }
      return
    }

    if (params.event.button !== 0) {
      return
    }

    if (this.activeGripEdit) {
      const targetPoint = this.getGripTargetPoint(params)
      this.previewGripEdit(targetPoint)
      this.commitGripEdit()
      return
    }

    const hoverGrip = this.getGripById(this.hoverGripId)
    if (hoverGrip) {
      this.beginGripEdit(hoverGrip)
    }
  }

  /**
   * 鼠标移动事件：处于控制点编辑时预览变更，否则刷新 hover 状态。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.activeTextEdit) {
      return
    }

    if (this.activeGripEdit) {
      this.previewGripEdit(this.getGripTargetPoint(params))
      return
    }

    this.updateHoverGrip(params.screenPos)
  }

  /**
   * 双击事件：在选中的多重引线上命中文字区域时进入文本编辑。
   *
   * @param params - 鼠标事件参数
   */
  onDoubleClick(params: MouseEventParams) {
    if (this.activeGripEdit || this.activeTextEdit) {
      return
    }

    const hit = this.controller.getCADIntersections(params.screenPos).find((intersection) => {
      const entity = intersection.object.userData?.entity as IEntity | undefined
      return (
        entity?.type === EntityType.MLeader &&
        intersection.object.userData?.hitRole === 'mleader-text'
      )
    })
    const entity = hit?.object.userData?.entity as MLeaderEntity | undefined
    if (!entity) {
      return
    }
    const isSelected = this.entitySelector
      .getSelected()
      .some((selectedEntity) => selectedEntity.id === entity.id)
    if (!isSelected) {
      return
    }
    params.event.preventDefault()
    params.event.stopPropagation()
    this.beginTextEdit(entity)
  }

  /**
   * 键盘按下事件：处理文本编辑/控制点编辑的 ESC 与 ENTER，以及 DELETE 删除选中。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (this.activeTextEdit) {
      if (params.event.keyCode === KeyCodes.ESC) {
        this.finishTextEdit(false)
      } else if (params.event.keyCode === KeyCodes.ENTER) {
        params.event.preventDefault()
        this.finishTextEdit(true)
      }
      return
    }

    if (params.event.keyCode === KeyCodes.ESC && this.activeGripEdit) {
      this.cancelGripEdit()
      return
    }

    if (params.event.keyCode === KeyCodes.DELETE) {
      if (this.activeGripEdit) {
        this.cancelGripEdit()
      }
      const selectedEntities = this.entitySelector.getSelected()
      this.entitySelector.clearSelection()
      this.historyManager.addCommandAndExecute(
        new DeleteEntityCommand(selectedEntities, this.viewManager)
      )
    }
  }

  /** 每帧更新控制点覆盖层缩放与文本编辑框位置 */
  onViewerUpdate() {
    this.gripOverlay?.updateScale()
    this.updateTextEditPosition()
  }

  /**
   * 选择变化回调：处理进行中的编辑是否被移除并刷新控制点。
   *
   * @param entities - 当前已选中的实体
   */
  onSelectorChanged(entities: IEntity[]) {
    if (
      this.activeGripEdit &&
      !entities.some((entity) => entity.id === this.activeGripEdit?.entity.id)
    ) {
      this.cancelGripEdit()
      return
    }
    if (
      this.activeTextEdit &&
      !entities.some((entity) => entity.id === this.activeTextEdit?.entity.id)
    ) {
      this.finishTextEdit(true)
      return
    }

    this.refreshGrips()
  }

  /************ 控制点编辑 ************/
  private refreshGrips() {
    const selectedEntities = this.entitySelector.getSelected()
    this.grips = selectedEntities.flatMap((entity) => EntityGripHandler.getGripPoints(entity))

    if (!this.getGripById(this.hoverGripId)) {
      this.hoverGripId = null
    }

    const activeGripId = this.activeGripEdit?.grip.id ?? null
    if (this.activeGripEdit && !this.getGripById(activeGripId)) {
      this.viewManager?.objectManager?.endEntityEdit?.(this.activeGripEdit.entity)
      this.activeGripEdit = null
      this.historyManager.setLock(false)
      this.auxiliaryMouseHelper.clearUI()
    }

    this.gripOverlay?.setGrips(this.grips)
    this.gripOverlay?.setHoverGrip(this.hoverGripId)
    this.gripOverlay?.setActiveGrip(activeGripId)
    this.updateSelectorInteraction()
  }

  private updateHoverGrip(screenPos: { x: number; y: number }) {
    let closestGrip: EntityGrip | null = null
    let minDistance = Number.POSITIVE_INFINITY
    const threshold = Setting.gripConfig.pixelRadius + Setting.gripConfig.hitPadding

    for (const grip of this.grips) {
      const gripScreenPos = this.controller.getScreenPosition(grip.point)
      const distance = Math.hypot(gripScreenPos.x - screenPos.x, gripScreenPos.y - screenPos.y)
      if (distance <= threshold && distance < minDistance) {
        minDistance = distance
        closestGrip = grip
      }
    }

    this.hoverGripId = closestGrip?.id ?? null
    this.gripOverlay?.setHoverGrip(this.hoverGripId)
    this.updateSelectorInteraction()
  }

  private updateSelectorInteraction() {
    const disableSelector = !!this.hoverGripId || !!this.activeGripEdit || !!this.activeTextEdit
    this.entitySelector.config({
      enableMouseSelect: !disableSelector,
      enableFrameSelect: !disableSelector,
    })
  }

  private beginGripEdit(grip: EntityGrip) {
    const entity = this.viewManager.getEntity(grip.entityId)
    if (!entity) {
      return
    }

    const originalData = EntityGripHandler.snapshot(entity)
    if (!originalData) {
      return
    }

    this.activeGripEdit = {
      entity,
      grip,
      originalData,
      targetPoint: cloneDeep(grip.point),
    }
    this.viewManager?.objectManager?.beginEntityEdit?.(entity)
    this.historyManager.setLock(true)
    this.hoverGripId = grip.id
    this.gripOverlay?.setHoverGrip(grip.id)
    this.gripOverlay?.setActiveGrip(grip.id)
    this.updateSelectorInteraction()
  }

  private previewGripEdit(targetPoint: IPoint) {
    if (!this.activeGripEdit) {
      return
    }

    this.activeGripEdit.targetPoint = cloneDeep(targetPoint)
    this.renderGripPreview()
    this.renderInvalidator.invalidate('cad.gripPreview.updated')
  }

  private commitGripEdit() {
    if (!this.activeGripEdit) {
      return
    }

    const { entity, grip, originalData, targetPoint } = this.activeGripEdit
    const isNoop = Math.hypot(targetPoint.x - grip.point.x, targetPoint.y - grip.point.y) < 1e-6
    if (isNoop) {
      this.finishGripEdit(true)
      return
    }

    let committed = false
    try {
      this.clearGripPreview()
      const command = new UpdateEntityByGripCommand(
        entity,
        grip,
        cloneDeep(targetPoint),
        cloneDeep(originalData),
        this.viewManager
      )
      this.historyManager.addCommandAndExecute(command)
      committed = true
    } finally {
      this.finishGripEdit(!committed)
    }
  }

  private cancelGripEdit() {
    this.finishGripEdit(true)
  }

  private finishGripEdit(restoreEntity: boolean) {
    const edit = this.activeGripEdit
    try {
      this.clearGripPreview()
      if (
        edit &&
        restoreEntity &&
        (!this.viewManager.getEntity || this.viewManager.getEntity(edit.entity.id) === edit.entity)
      ) {
        Object.assign(edit.entity, cloneDeep(edit.originalData))
        this.viewManager.updateEntity(edit.entity, { updateGeometry: true })
      }
    } finally {
      this.activeGripEdit = null
      try {
        if (edit) this.viewManager?.objectManager?.endEntityEdit?.(edit.entity)
      } finally {
        if (!this.activeTextEdit) this.historyManager.setLock(false)
        this.auxiliaryMouseHelper.clearUI()
        if (edit) this.refreshGrips()
        else this.updateSelectorInteraction()
      }
    }
  }

  private renderGripPreview() {
    if (!this.activeGripEdit || !this.gripPreviewObjectManager) {
      return
    }

    this.clearGripPreview()
    const { entity, grip, originalData, targetPoint } = this.activeGripEdit
    const previewEntity = cloneDeep(entity)
    Object.assign(previewEntity, cloneDeep(originalData))
    EntityGripHandler.applyGrip(previewEntity, grip, targetPoint)
    this.gripPreviewObjectManager.addEntity(previewEntity)
    this.renderGripPreviewLine(grip.point, targetPoint)
  }

  private clearGripPreview() {
    const hadPreview =
      !!this.gripPreviewLine ||
      (this.gripPreviewObjectManager?.getSceneGroup().children.length ?? 0) > 0
    this.gripPreviewObjectManager?.deleteAllEntities()
    this.clearGripPreviewLine()
    if (hadPreview) {
      this.renderInvalidator.invalidate('cad.gripPreview.cleared')
    }
  }

  private renderGripPreviewLine(startPoint: IPoint, endPoint: IPoint) {
    if (!this.gripPreviewLine) {
      const dashSize = this.controller.getPixelSizeInWorld(Setting.dashSize)
      const materialOption = {
        color: Setting.leadLineConfig.color,
        opacity: Setting.leadLineConfig.opacity,
        transparent: Setting.leadLineConfig.opacity < 1,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.gripPreviewLine = DrawUtils.drawDashedLine(startPoint, endPoint, materialOption)
      this.viewManager.attachTempObject(this.gripPreviewLine)
      return
    }

    DrawUtils.updateLineByPoints(this.gripPreviewLine, [startPoint, endPoint])
    this.gripPreviewLine.computeLineDistances()
  }

  private clearGripPreviewLine() {
    if (!this.gripPreviewLine) {
      return
    }
    DrawUtils.disposeObject(this.gripPreviewLine)
    this.gripPreviewLine = undefined
  }

  private clearGripHover() {
    this.hoverGripId = null
    this.gripOverlay?.setHoverGrip(null)
  }

  private getGripTargetPoint(params: MouseEventParams): IPoint {
    const startPoint = this.activeGripEdit?.grip.point
    const targetPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })
    return {
      x: targetPoint.x,
      y: targetPoint.y,
    }
  }

  private getGripById(gripId: string | null) {
    if (!gripId) {
      return null
    }
    return this.grips.find((grip) => grip.id === gripId) || null
  }

  /************ 文本编辑 ************/
  private beginTextEdit(entity: MLeaderEntity) {
    this.finishTextEdit(true)
    const inputElement = this.createTextEditInput(entity)
    this.activeTextEdit = {
      entity,
      originalContents: entity.contents,
      inputElement,
    }
    this.historyManager.setLock(true)
    this.updateSelectorInteraction()
  }

  private finishTextEdit(commit: boolean) {
    if (!this.activeTextEdit) {
      if (!this.activeGripEdit) {
        this.historyManager.setLock(false)
      }
      this.updateSelectorInteraction()
      return
    }

    const { entity, originalContents, inputElement } = this.activeTextEdit
    const nextContents = inputElement.value.trim()
    this.activeTextEdit = null
    inputElement.remove()

    if (commit && nextContents && nextContents !== originalContents) {
      this.historyManager.addCommandAndExecute(
        new UpdateEntityCommand(
          [entity],
          { contents: nextContents },
          { updateGeometry: true, updateMaterial: false },
          this.viewManager
        )
      )
    }

    if (!this.activeGripEdit) {
      this.historyManager.setLock(false)
    }
    this.refreshGrips()
  }

  private createTextEditInput(entity: MLeaderEntity) {
    const inputElement = document.createElement('textarea')
    Object.assign(inputElement.style, {
      position: 'absolute',
      minWidth: '160px',
      minHeight: '48px',
      padding: '6px 8px',
      outline: 'none',
      resize: 'none',
      border: '1px solid #d0d0d0',
      backgroundColor: '#fff',
      color: '#000',
      transform: 'translate(0, -50%)',
    })
    inputElement.value = entity.contents
    inputElement.addEventListener('keydown', (event) => {
      event.stopPropagation()
      if (event.keyCode === KeyCodes.ENTER && !event.shiftKey) {
        event.preventDefault()
        this.finishTextEdit(true)
      } else if (event.keyCode === KeyCodes.ESC) {
        event.preventDefault()
        this.finishTextEdit(false)
      }
    })
    inputElement.addEventListener('mousedown', (event) => {
      event.stopPropagation()
    })
    inputElement.addEventListener('dblclick', (event) => {
      event.stopPropagation()
    })
    inputElement.addEventListener('contextmenu', (event) => {
      event.preventDefault()
    })
    inputElement.addEventListener('blur', () => {
      this.finishTextEdit(true)
    })
    this.controller.domElement?.parentElement?.appendChild(inputElement)
    this.updateTextEditPosition(entity, inputElement)
    inputElement.focus()
    inputElement.select()
    return inputElement
  }

  private updateTextEditPosition(
    entity = this.activeTextEdit?.entity,
    inputElement = this.activeTextEdit?.inputElement
  ) {
    if (!entity || !inputElement) {
      return
    }
    const screenPos = this.controller.getScreenPosition(entity.textPosition)
    Object.assign(inputElement.style, {
      left: `${screenPos.x}px`,
      top: `${screenPos.y}px`,
    })
  }
}
